/**
 * Biometric Authentication Service
 * 
 * Provides Face ID, Touch ID, and PIN fallback authentication
 * for securing document access.
 */

import * as LocalAuthentication from 'expo-local-authentication';
import * as SecureStore from 'expo-secure-store';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Platform } from 'react-native';

const PIN_STORAGE_KEY = 'tripflow_document_pin';
const BIOMETRIC_ENABLED_KEY = 'tripflow_biometric_enabled';
const PIN_ENABLED_KEY = 'tripflow_pin_enabled';
const WEB_PIN_STORAGE_KEY = 'tripflow_document_pin_web';
const WEB_PIN_ENABLED_KEY = 'tripflow_pin_enabled_web';

const isWeb = Platform.OS === 'web';

/**
 * Secure storage helpers with web fallback
 */
async function setItemSecure(key: string, value: string): Promise<void> {
  if (isWeb) {
    try {
      await AsyncStorage.setItem(key, value);
    } catch {
      // Fallback to localStorage if AsyncStorage is unavailable in web build
      try {
        window.localStorage.setItem(key, value);
      } catch {
        // swallow; web secure storage unavailable
      }
    }
    return;
  }
  await SecureStore.setItemAsync(key, value);
}

async function getItemSecure(key: string): Promise<string | null> {
  if (isWeb) {
    try {
      const value = await AsyncStorage.getItem(key);
      if (value !== null) return value;
    } catch {
      // ignore
    }
    try {
      return window.localStorage.getItem(key);
    } catch {
      return null;
    }
  }
  return SecureStore.getItemAsync(key);
}

async function deleteItemSecure(key: string): Promise<void> {
  if (isWeb) {
    try {
      await AsyncStorage.removeItem(key);
    } catch {
      // ignore
    }
    try {
      window.localStorage.removeItem(key);
    } catch {
      // ignore
    }
    return;
  }
  await SecureStore.deleteItemAsync(key);
}

export type BiometricType = 'face' | 'fingerprint' | 'iris' | 'none';

export interface BiometricAuthResult {
  success: boolean;
  error?: string;
  biometricType?: BiometricType;
}

export interface BiometricAvailability {
  isAvailable: boolean;
  types: BiometricType[];
  hasHardware: boolean;
  isEnrolled: boolean;
}

/**
 * Check what biometric authentication methods are available
 */
export async function checkBiometricAvailability(): Promise<BiometricAvailability> {
  try {
    const hasHardware = await LocalAuthentication.hasHardwareAsync();
    const isEnrolled = await LocalAuthentication.isEnrolledAsync();
    const supportedTypes = await LocalAuthentication.supportedAuthenticationTypesAsync();

    const types: BiometricType[] = [];
    if (supportedTypes.includes(LocalAuthentication.AuthenticationType.FACIAL_RECOGNITION)) {
      types.push('face');
    }
    if (supportedTypes.includes(LocalAuthentication.AuthenticationType.FINGERPRINT)) {
      types.push('fingerprint');
    }
    if (supportedTypes.includes(LocalAuthentication.AuthenticationType.IRIS)) {
      types.push('iris');
    }

    return {
      isAvailable: hasHardware && isEnrolled && types.length > 0,
      types,
      hasHardware,
      isEnrolled,
    };
  } catch (error) {
    console.error('[BiometricAuth] Error checking availability:', error);
    return {
      isAvailable: false,
      types: [],
      hasHardware: false,
      isEnrolled: false,
    };
  }
}

/**
 * Authenticate using biometrics (Face ID, Touch ID, etc.)
 */
export async function authenticateWithBiometrics(
  reason: string = 'Authenticate to access your documents'
): Promise<BiometricAuthResult> {
  try {
    const availability = await checkBiometricAvailability();
    
    if (!availability.isAvailable) {
      return {
        success: false,
        error: 'Biometric authentication is not available on this device',
      };
    }

    const result = await LocalAuthentication.authenticateAsync({
      promptMessage: reason,
      cancelLabel: 'Cancel',
      disableDeviceFallback: false, // Allow device passcode as fallback
      fallbackLabel: 'Use PIN',
    });

    if (result.success) {
      const types = availability.types;
      return {
        success: true,
        biometricType: types[0] || 'none',
      };
    } else {
      return {
        success: false,
        error: result.error === 'user_cancel' 
          ? 'Authentication cancelled' 
          : 'Authentication failed',
      };
    }
  } catch (error) {
    console.error('[BiometricAuth] Error during authentication:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Authentication error',
    };
  }
}

/**
 * Store PIN securely
 */
export async function setPIN(pin: string): Promise<boolean> {
  try {
    const pinKey = isWeb ? WEB_PIN_STORAGE_KEY : PIN_STORAGE_KEY;
    const enabledKey = isWeb ? WEB_PIN_ENABLED_KEY : PIN_ENABLED_KEY;
    await setItemSecure(pinKey, pin);
    await setItemSecure(enabledKey, 'true');
    return true;
  } catch (error) {
    console.error('[BiometricAuth] Error storing PIN:', error);
    return false;
  }
}

/**
 * Verify PIN
 */
export async function verifyPIN(pin: string): Promise<boolean> {
  try {
    const pinKey = isWeb ? WEB_PIN_STORAGE_KEY : PIN_STORAGE_KEY;
    const storedPIN = await getItemSecure(pinKey);
    return storedPIN === pin;
  } catch (error) {
    console.error('[BiometricAuth] Error verifying PIN:', error);
    return false;
  }
}

/**
 * Check if PIN is set
 */
export async function hasPIN(): Promise<boolean> {
  try {
    const enabledKey = isWeb ? WEB_PIN_ENABLED_KEY : PIN_ENABLED_KEY;
    const pinEnabled = await getItemSecure(enabledKey);
    return pinEnabled === 'true';
  } catch (error) {
    return false;
  }
}

/**
 * Remove PIN
 */
export async function removePIN(): Promise<boolean> {
  try {
    const pinKey = isWeb ? WEB_PIN_STORAGE_KEY : PIN_STORAGE_KEY;
    const enabledKey = isWeb ? WEB_PIN_ENABLED_KEY : PIN_ENABLED_KEY;
    await deleteItemSecure(pinKey);
    await deleteItemSecure(enabledKey);
    return true;
  } catch (error) {
    console.error('[BiometricAuth] Error removing PIN:', error);
    return false;
  }
}

/**
 * Enable/disable biometric authentication
 */
export async function setBiometricEnabled(enabled: boolean): Promise<boolean> {
  try {
    await setItemSecure(BIOMETRIC_ENABLED_KEY, enabled ? 'true' : 'false');
    return true;
  } catch (error) {
    console.error('[BiometricAuth] Error setting biometric enabled:', error);
    return false;
  }
}

/**
 * Check if biometric authentication is enabled
 */
export async function isBiometricEnabled(): Promise<boolean> {
  try {
    const enabled = await getItemSecure(BIOMETRIC_ENABLED_KEY);
    // Default to true if not set (first time setup)
    return enabled !== 'false';
  } catch (error) {
    return true; // Default to enabled
  }
}

/**
 * Get authentication preferences
 */
export async function getAuthPreferences(): Promise<{
  biometricEnabled: boolean;
  pinEnabled: boolean;
  biometricAvailable: boolean;
}> {
  const [biometricEnabled, pinEnabled, availability] = await Promise.all([
    isBiometricEnabled(),
    hasPIN(),
    checkBiometricAvailability(),
  ]);

  return {
    biometricEnabled,
    pinEnabled,
    biometricAvailable: availability.isAvailable,
  };
}

/**
 * Authenticate user (tries biometric first, then PIN if available)
 */
export async function authenticate(
  reason: string = 'Authenticate to access your documents'
): Promise<BiometricAuthResult> {
  const preferences = await getAuthPreferences();

  // Try biometric first if enabled and available
  if (preferences.biometricEnabled && preferences.biometricAvailable) {
    const biometricResult = await authenticateWithBiometrics(reason);
    if (biometricResult.success) {
      return biometricResult;
    }
    // If biometric fails but user cancelled, return the error
    if (biometricResult.error === 'Authentication cancelled') {
      return biometricResult;
    }
  }

  // Fallback to PIN if available
  if (preferences.pinEnabled) {
    // Return a special result indicating PIN is needed
    return {
      success: false,
      error: 'PIN_REQUIRED',
    };
  }

  // No authentication method available
  return {
    success: false,
    error: 'No authentication method configured',
  };
}

/**
 * Get user-friendly name for biometric type
 */
export function getBiometricTypeName(type: BiometricType): string {
  switch (type) {
    case 'face':
      return Platform.OS === 'ios' ? 'Face ID' : 'Face Recognition';
    case 'fingerprint':
      return Platform.OS === 'ios' ? 'Touch ID' : 'Fingerprint';
    case 'iris':
      return 'Iris';
    default:
      return 'Biometric';
  }
}

